import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  TextField,
  IconButton,
  Autocomplete,
  Divider,
  FormHelperText,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import axios from "axios";
import SearchIcon from "@mui/icons-material/Search";
import InventoryIcon from '@mui/icons-material/Inventory';
import EditPage from "./EditPage";
import KeyboardReturnIcon from "@mui/icons-material/KeyboardReturn";
import { fetchForInput, fetchProducts, validateDecimal, validateNonEmpty, handleError } from "../../utils/lib";
import { useAppBar } from '../../providers/AppBarProvider';
import { useTranslation } from 'react-i18next';
import ListItemProductRemove from "../../layouts/ListItemProductRemove";
import { useSnackbar } from "../../providers/SnackBarProvider";
import ProductUnitSelector from "../../layouts/ProductUnitSelector";
import useMovementStore from "../../store/movementStore";

const EditMovement = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const [isLoading, setIsLoading] = useState(true);
  const [deposits, setDeposits] = useState([]);
  const [movementPoints, setMovementPoints] = useState([]);
  const [products, setProducts] = useState([]);
  const {updateTitle, toggleFab} = useAppBar();

  // Use movement store
  const {
    movement,
    movementItem,
    updateMovement,
    setMovementItem,
    addItem,
    removeItem,
    resetMovement,
    resetMovementItem,
  } = useMovementStore();

  // References for form fields
  const [errors, setErrors] = useState({});
  const refs = {
    product: useRef(),
    quantity: useRef(),
    originDeposit: useRef(),
    destinationDeposit: useRef(),
    movementPoint: useRef(),
  };

  // Load initial data
  useEffect(() => {
    updateTitle('', t('edit.movementTitle'), t('edit.movementCompleteData'));
    toggleFab(false);

    const fetchData = async () => {
      const movementsForInput = await fetchForInput("internal_movements", "movements_for_input");
      setDeposits(movementsForInput.deposits);
      setMovementPoints(movementsForInput.movement_points);
      const products = await fetchProducts();
      setProducts(products);

      setIsLoading(false);

      setTimeout(() => {
        if (refs.product.current) {
          refs.product.current.focus();
        }
      }, 100);
    };
    fetchData();
  }, []);

  // Set default values for deposits
  useEffect(() => {
    if (deposits.length > 0) {
      // Only set defaults if they haven't been set already
      if (!movement.origin_deposit) {
        updateMovement({ origin_deposit: deposits[0] });
      }
      if (!movement.destination_deposit) {
        // Set second deposit as default if available, otherwise use the first one
        updateMovement({ destination_deposit: deposits.length > 1 ? deposits[1] : deposits[0] });
      }
    }
  }, [deposits]);

  // Set default movement point
  useEffect(() => {
    if (movementPoints.length > 0 && !movement.movement_point) {
      updateMovement({ movement_point: movementPoints[0] });
    }
  }, [movementPoints]);

  const handleSearchProduct = () => {
    navigate("/main/search-product", { state: { from: "/main/edit-movement" } });
  };

  // Add product: validate fields and add to movement
  const handleAddProduct = () => {
    if (!movementItem.product || !movementItem.quantity) {
      snackbar(`${t('common.select')} ${t('common.productService').toLowerCase()} ${t('common.and')} ${t('common.quantity').toLowerCase()}`);
      return;
    }

    if (!validateDecimal("quantity", movementItem.quantity, setErrors, refs.quantity)) {
      return;
    }

    addItem(structuredClone(movementItem));
    resetMovementItem();

    refs.product.current.focus();
  };

  // Remove product
  const handleRemoveProduct = (index) => {
    removeItem(index);
  };

  const validateDeposits = () => {
    // Check if origin and destination deposits are the same
    if (movement.origin_deposit?.id === movement.destination_deposit?.id) {
      snackbar(t('edit.depositsError'));
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!validateNonEmpty("origin_deposit", movement.origin_deposit?.name, setErrors, refs.originDeposit)) {
      return;
    }

    if (!validateNonEmpty("destination_deposit", movement.destination_deposit?.name, setErrors, refs.destinationDeposit)) {
      return;
    }

    // Check if deposits are different
    if (!validateDeposits()) {
      return;
    }

    if (!movement.items || movement.items.length === 0) {
      snackbar(`${t('common.addAtLeastOne')} ${t('common.productService').toLowerCase()}`);
      return;
    }

    save();
  };

  const save = async () => {
    setIsLoading(true);
    try {
      // Format the date to MySQL datetime format before sending
      const movementToSend = {
        ...movement,
        date: dayjs(movement.date).format('YYYY-MM-DD HH:mm:ss')
      };
      
      const response = await axios.post(`${import.meta.env.VITE_API_URL}internal_movements`, 
        movementToSend, 
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
        }
      });

      resetMovement();
      
      // Set default deposits
      if (deposits.length > 0) {
        updateMovement({ 
          origin_deposit: deposits[0],
          destination_deposit: deposits.length > 1 ? deposits[1] : deposits[0]
        });
      }

      // Set default movement point
      if (movementPoints.length > 0) {
        updateMovement({ movement_point: movementPoints[0] });
      }

      snackbar(t('edit.movementSaved'));
      if (refs.product.current) {
        setTimeout(() => {
          refs.product.current.focus();
        }, 100);
      }
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
    }
  };

  const manageInsertMovementItem = (product) => {
    if (product) {
      setMovementItem({
        product: product,
        quantity: (1).toFixed(2),
        selected_unit: product.unit.first_unit_string,
      });

      setTimeout(() => {
        if (refs.quantity.current) {
          refs.quantity.current.focus();
          refs.quantity.current.select();
        }
      }, 100);
      
    } else {
      resetMovementItem();
    }
  };

  return (
    <EditPage
      onSubmit={handleSubmit}
      isLoading={isLoading}
    >
      <Box sx={{ width: "100%", marginTop: 1 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DateTimePicker
            label={t('common.dateTime')}
            value={movement.date}
            onChange={(newValue) => updateMovement({ date: newValue })}
            renderInput={(params) => <TextField {...params} fullWidth />}
            slotProps={{ textField: { fullWidth: true } }}
            format="DD/MM/YYYY HH:mm"
          />
        </LocalizationProvider>
      </Box>

      <Box
        display="flex"
        gap={2}
        width="100%"
        justifyContent="center"
        sx={{ marginY: 3 }}
      >
        <Box flex={1}>
          <Autocomplete
            options={deposits}
            getOptionLabel={(option) => option ? `${option.name} - ${option.vet.name}` : ""}
            value={movement.origin_deposit}
            onChange={(_, newValue) => updateMovement({ origin_deposit: newValue })}
            renderInput={(params) => (
              <TextField 
                {...params} 
                label={t('edit.sourceDeposit')} 
                fullWidth 
                inputRef={refs.originDeposit}
                error={!!errors.origin_deposit}
                helperText={errors.origin_deposit}
              />
            )}
            freeSolo={false}
          />
        </Box>
        <Box flex={1}>
          <Autocomplete
            options={deposits}
            getOptionLabel={(option) => option ? `${option.name} - ${option.vet.name}` : ""}
            value={movement.destination_deposit}
            onChange={(_, newValue) => updateMovement({ destination_deposit: newValue })}
            renderInput={(params) => (
              <TextField 
                {...params} 
                label={t('edit.targetDeposit')} 
                fullWidth 
                inputRef={refs.destinationDeposit}
                error={!!errors.destination_deposit}
                helperText={errors.destination_deposit}
              />
            )}
            freeSolo={false}
          />
        </Box>
      </Box>

      {movementPoints.length > 1 && (
        <Box mb={3}>
          <Autocomplete
            options={movementPoints}
            getOptionLabel={(option) => option.name}
            value={movement.movement_point}
            onChange={(_, newValue) => updateMovement({ movement_point: newValue })}
            renderInput={(params) => (
              <TextField 
                {...params} 
                label={t('edit.movementPoint')} 
                fullWidth 
                inputRef={refs.movementPoint}
              />
            )}
            freeSolo={false}
          />
        </Box>
      )}

      {movement.origin_deposit?.id === movement.destination_deposit?.id && (
        <FormHelperText error>
          {t('edit.depositsDifferentError')}
        </FormHelperText>
      )}

      <Divider sx={{ marginY: 2 }} />

      <Box display="flex" gap={1} alignItems="center">
        {/* Product autocomplete with search functionality */}
        <Autocomplete
          fullWidth
          options={products}
          getOptionLabel={(option) => (option ? option.name : "")}
          value={movementItem?.product}
          onChange={(e, newValue) => {
            manageInsertMovementItem(newValue);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('common.productService')}
              name="product"
              margin="normal"
              inputRef={refs.product}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  setTimeout(() => {
                    if (!refs.product.current?.value) {
                      handleSubmit(e);
                    }
                  }, 100);
                }
              }}
            />
          )}
        />

        {/* Product search button */}
        <IconButton onClick={handleSearchProduct} sx={{ marginTop: 1 }}>
          <SearchIcon />
        </IconButton>

        {/* Quantity input field */}
        <TextField
          label={t('common.quantity')}
          type="text"
          inputProps={{ inputMode: "decimal", pattern: "[0-9]*[.,]?[0-9]*" }}
          value={movementItem?.quantity}
          onChange={(e) => {
            const value = e.target.value.replace(/[^0-9.,]/g, "");
            setMovementItem({ ...movementItem, quantity: value });
          }}
          sx={{ marginTop: 1, "& .MuiInputBase-input": { textAlign: "right" } }}
          inputRef={refs.quantity}
          error={!!errors.quantity}
          helperText={errors.quantity}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleAddProduct();
            }
          }}
        />

        {/* Unit selector - Only displayed for products with complex units */}
        {movementItem.product?.unit.is_complex === 1 && (
          <ProductUnitSelector
            product={movementItem.product}
            sellItem={movementItem} // Reusing the same component that expects sellItem
            setSellItem={setMovementItem} // Reusing the same component
          />
        )}

        <IconButton onClick={handleAddProduct} sx={{ marginTop: 1 }}>
          <KeyboardReturnIcon />
        </IconButton>
      </Box>

      {/* Product list */}  
      <Box>
        {movement.items?.map((item, index) => (
          <ListItemProductRemove
            key={index}
            product={item.product.name}
            description={`${t('common.quantity')}: ${item.quantity}${item.selected_unit ? ' ' + item.selected_unit : ''}`}
            subtotal=""
            onDelete={handleRemoveProduct}
            index={index}
            thumbUrl={item.product.thumb_url}
            avatarContent={<InventoryIcon sx={{ fontSize: 20 }} />}
          />
        ))}
      </Box>

    </EditPage>
  );
};

export default EditMovement; 